"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import MenuOverlay from "../menu-overlay/MenuOverlay"; // <-- import the reusable overlay

/** ---- Strapi media (image) ---- */
type StrapiImage = {
  id: number;
  url: string;
  name: string;
  alternativeText?: string | null;
  caption?: string | null;
  ext?: string | null;
  mime?: string | null;
  size?: number | null;
  width?: number | null;
  height?: number | null;
};

function absoluteCmsUrl(path?: string | null) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const base = process.env.NEXT_PUBLIC_STRAPI_URL || "";
  return `${base}${path}`;
}

type HeroMainProps = {
  title?: string | null;
  description?: unknown;
  imageURL?: string | null;
  image?: StrapiImage | null;

  /** Optional href for Contact (Menu is now an overlay trigger) */
  contactHref?: string;
  className?: string;
};

export default function HeroMain({
  title = null,
  description = null,
  imageURL = null,
  image = null,
  contactHref = "/contact",
  className = "",
}: HeroMainProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef<HTMLButtonElement | null>(null);
  const contactRef = useRef<HTMLAnchorElement | null>(null);

  const reducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  /** Prefer media.url from Strapi if provided, else explicit imageURL */
  const resolvedImageUrl = image?.url ? absoluteCmsUrl(image.url) : imageURL;

  // GSAP entrance animation for side labels
  useEffect(() => {
    if (reducedMotion) return;
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    tl.fromTo(
      menuRef.current,
      { x: -16, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, duration: 0.5 },
    ).fromTo(
      contactRef.current,
      { x: 16, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, duration: 0.5 },
      "-=0.25",
    );
    return () => {
      tl.kill();
    };
  }, [reducedMotion]);

  return (
    <>
      <section
        className={`max-w-7xl w-full mx-auto relative reveal ${className}`}
        aria-label={title || "Featured"}
      >
        {/* Left vertical trigger (button now) */}
        <button
          ref={menuRef}
          type="button"
          onClick={() => setMenuOpen(true)}
          className="hidden xl:block absolute cursor-pointer -left-20 inset-y-1/2 left-rotate-text font-normal opacity-0 hover:underline underline-offset-2"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          aria-controls="preintro-menu"
          aria-label="Open menu"
        >
          Menu
        </button>

        {/* Image */}
        <div className="w-full aspect-video md:aspect-[16/9] min-h-[450px] relative">
          <Image
            src={
              resolvedImageUrl ||
              "https://cms.lmsgroup.phixela.com/uploads/01_2_ed4b35fb47.webp"
            }
            className="w-full h-full object-cover absolute inset-0"
            width={image?.width || 2000}
            height={image?.height || 1125}
            alt={image?.alternativeText || title || "Main Lucerne"}
            priority
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
            }}
          />
        </div>

        {/* Right vertical link */}
        <Link
          href={contactHref}
          ref={contactRef}
          className="hidden xl:block absolute -right-20 inset-y-1/2 right-rotate-text font-normal opacity-0 hover:underline underline-offset-2"
          aria-label="Contact"
        >
          Contact
        </Link>
      </section>

      {/* Reusable overlay */}
      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
